import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  User, Briefcase, CreditCard, MapPin, Home as HomeIcon, Users, LifeBuoy, Save, Plus, Trash2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ------------------------- shared field component ------------------------- */
function Field({ label, type = 'text', value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider mb-2 block">{label}</Label>
      <Input
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="font-mono border-border bg-transparent border-b-2 rounded-none focus:border-primary"
        data-testid={testid}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider mb-2 block">{label}</Label>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full font-mono bg-transparent border-b-2 border-border focus:border-primary rounded-none py-2 px-1 text-sm outline-none"
        data-testid={testid}
      >
        <option value="">— Select —</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border pb-2 mb-4 mt-2">
      {children}
    </div>
  );
}

/* --------------------------------- forms --------------------------------- */

function IdentityForm({ data, onSaved }) {
  const [f, setF] = useState({
    full_name: data.full_name || '',
    first_name: data.first_name || '',
    middle_name: data.middle_name || '',
    last_name: data.last_name || '',
    email: data.email || '',
    phone: data.phone || '',
    phone_alt: data.phone_alt || '',
    date_of_birth: data.date_of_birth || '',
    gender: data.gender || '',
    marital_status: data.marital_status || '',
    nationality: data.nationality || '',
    driver_licence_number: data.driver_licence_number || '',
    driver_licence_state: data.driver_licence_state || '',
    passport_number: data.passport_number || '',
    medicare_number: data.medicare_number || '',
    visa_status: data.visa_status || '',
    right_to_work: data.right_to_work || '',
    linkedin_url: data.linkedin_url || '',
    portfolio_url: data.portfolio_url || '',
    addresses: data.addresses || [],
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setF({ ...f, [k]: v });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/profile/identity`, f, { withCredentials: true });
      toast.success('Identity updated');
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="identity-form">
      <SectionHeader>PERSONAL</SectionHeader>
      <div className="grid md:grid-cols-2 gap-6">
        <Field label="FULL NAME" value={f.full_name} onChange={(v) => set('full_name', v)} testid="input-full-name" />
        <Field label="EMAIL" type="email" value={f.email} onChange={(v) => set('email', v)} testid="input-email" />
        <Field label="FIRST NAME" value={f.first_name} onChange={(v) => set('first_name', v)} testid="input-first-name" />
        <Field label="MIDDLE NAME" value={f.middle_name} onChange={(v) => set('middle_name', v)} testid="input-middle-name" />
        <Field label="LAST NAME" value={f.last_name} onChange={(v) => set('last_name', v)} testid="input-last-name" />
        <Field label="PHONE (mobile)" type="tel" value={f.phone} onChange={(v) => set('phone', v)} testid="input-phone" />
        <Field label="ALT PHONE" type="tel" value={f.phone_alt} onChange={(v) => set('phone_alt', v)} testid="input-phone-alt" />
        <Field label="DATE OF BIRTH" type="date" value={f.date_of_birth} onChange={(v) => set('date_of_birth', v)} testid="input-dob" />
        <Select label="GENDER" value={f.gender} onChange={(v) => set('gender', v)} options={['Male', 'Female', 'Non-binary', 'Prefer not to say']} testid="select-gender" />
        <Select label="MARITAL STATUS" value={f.marital_status} onChange={(v) => set('marital_status', v)} options={['Single', 'Married', 'De facto', 'Divorced', 'Separated', 'Widowed']} testid="select-marital" />
        <Field label="NATIONALITY" value={f.nationality} onChange={(v) => set('nationality', v)} placeholder="e.g. Australian" testid="input-nationality" />
      </div>

      <SectionHeader>IDENTITY DOCUMENTS</SectionHeader>
      <div className="grid md:grid-cols-2 gap-6">
        <Field label="DRIVER'S LICENCE #" value={f.driver_licence_number} onChange={(v) => set('driver_licence_number', v)} testid="input-licence" />
        <Field label="LICENCE STATE" value={f.driver_licence_state} onChange={(v) => set('driver_licence_state', v)} placeholder="e.g. NSW" testid="input-licence-state" />
        <Field label="PASSPORT #" value={f.passport_number} onChange={(v) => set('passport_number', v)} testid="input-passport" />
        <Field label="MEDICARE #" value={f.medicare_number} onChange={(v) => set('medicare_number', v)} testid="input-medicare" />
      </div>

      <SectionHeader>WORK ELIGIBILITY & LINKS</SectionHeader>
      <div className="grid md:grid-cols-2 gap-6">
        <Select label="RIGHT TO WORK" value={f.right_to_work} onChange={(v) => set('right_to_work', v)} options={['Australian Citizen', 'Permanent Resident', 'Work Visa', 'Student Visa', 'No']} testid="select-right-to-work" />
        <Field label="VISA STATUS / SUBCLASS" value={f.visa_status} onChange={(v) => set('visa_status', v)} placeholder="e.g. 482, 500, Citizen" testid="input-visa" />
        <Field label="LINKEDIN URL" type="url" value={f.linkedin_url} onChange={(v) => set('linkedin_url', v)} testid="input-linkedin" />
        <Field label="PORTFOLIO / WEBSITE" type="url" value={f.portfolio_url} onChange={(v) => set('portfolio_url', v)} testid="input-portfolio" />
      </div>

      <Button type="submit" disabled={saving} className="brutalist-shadow-sm uppercase text-xs tracking-wider" data-testid="save-identity-btn">
        <Save className="w-4 h-4 mr-2" /> {saving ? 'SAVING...' : 'SAVE IDENTITY'}
      </Button>
    </form>
  );
}

function AddressForm({ data, onSaved }) {
  // Identity holds `addresses` array; we manage index 0 (current) here
  const current = (data.addresses && data.addresses[0]) || {};
  const [f, setF] = useState({
    street: current.street || '',
    line2: current.line2 || '',
    city: current.city || '',
    state: current.state || '',
    postcode: current.postcode || '',
    country: current.country || 'Australia',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const full = [f.street, f.line2, f.city, f.state, f.postcode, f.country].filter(Boolean).join(', ');
      const nextAddresses = [{ ...f, full_address: full }, ...(data.addresses || []).slice(1)];
      await axios.put(`${API}/profile/identity`, { ...data, addresses: nextAddresses }, { withCredentials: true });
      toast.success('Address saved');
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="address-form">
      <SectionHeader>CURRENT RESIDENTIAL ADDRESS</SectionHeader>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <Field label="STREET ADDRESS" value={f.street} onChange={(v) => set('street', v)} placeholder="e.g. 12 Elizabeth Street" testid="input-street" />
        </div>
        <Field label="APT / UNIT / SUITE" value={f.line2} onChange={(v) => set('line2', v)} placeholder="Unit 5" testid="input-line2" />
        <Field label="SUBURB / CITY" value={f.city} onChange={(v) => set('city', v)} testid="input-city" />
        <Field label="STATE" value={f.state} onChange={(v) => set('state', v)} placeholder="NSW / VIC / QLD..." testid="input-state" />
        <Field label="POSTCODE" value={f.postcode} onChange={(v) => set('postcode', v)} testid="input-postcode" />
        <Field label="COUNTRY" value={f.country} onChange={(v) => set('country', v)} testid="input-country" />
      </div>
      <Button type="submit" disabled={saving} className="brutalist-shadow-sm uppercase text-xs tracking-wider" data-testid="save-address-btn">
        <Save className="w-4 h-4 mr-2" /> {saving ? 'SAVING...' : 'SAVE ADDRESS'}
      </Button>
    </form>
  );
}

function WorkForm({ data, onSaved }) {
  const [f, setF] = useState({
    current_employer: data.current_employer || '',
    job_title: data.job_title || '',
    employment_type: data.employment_type || '',
    industry: data.industry || '',
    employment_start_date: data.employment_start_date || '',
    years_experience: data.years_experience || 0,
    employer_phone: data.employer_phone || '',
    employer_email: data.employer_email || '',
    employer_address: data.employer_address || '',
    manager_name: data.manager_name || '',
    notice_period: data.notice_period || '',
    availability_date: data.availability_date || '',
    expected_salary: data.expected_salary || '',
    willing_to_relocate: data.willing_to_relocate || '',
    cover_letter: data.cover_letter || '',
    skills: data.skills || [],
    certifications: data.certifications || [],
  });
  const [skillInput, setSkillInput] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });

  const addSkill = () => {
    if (skillInput.trim()) {
      set('skills', [...f.skills, skillInput.trim()]);
      setSkillInput('');
    }
  };
  const removeSkill = (i) => set('skills', f.skills.filter((_, x) => x !== i));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/profile/work`, { ...f, years_experience: parseInt(f.years_experience) || 0 }, { withCredentials: true });
      toast.success('Work info saved');
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="work-form">
      <SectionHeader>CURRENT EMPLOYMENT</SectionHeader>
      <div className="grid md:grid-cols-2 gap-6">
        <Field label="CURRENT EMPLOYER" value={f.current_employer} onChange={(v) => set('current_employer', v)} testid="input-employer" />
        <Field label="JOB TITLE" value={f.job_title} onChange={(v) => set('job_title', v)} testid="input-job-title" />
        <Select label="EMPLOYMENT TYPE" value={f.employment_type} onChange={(v) => set('employment_type', v)} options={['Full-time', 'Part-time', 'Casual', 'Contract', 'Self-employed', 'Freelance', 'Unemployed', 'Student']} testid="select-employment-type" />
        <Field label="INDUSTRY" value={f.industry} onChange={(v) => set('industry', v)} testid="input-industry" />
        <Field label="EMPLOYMENT START DATE" type="date" value={f.employment_start_date} onChange={(v) => set('employment_start_date', v)} testid="input-emp-start" />
        <Field label="YEARS OF EXPERIENCE" type="number" value={f.years_experience} onChange={(v) => set('years_experience', v)} testid="input-yoe" />
        <Field label="EMPLOYER PHONE" type="tel" value={f.employer_phone} onChange={(v) => set('employer_phone', v)} testid="input-employer-phone" />
        <Field label="EMPLOYER EMAIL" type="email" value={f.employer_email} onChange={(v) => set('employer_email', v)} testid="input-employer-email" />
        <div className="md:col-span-2">
          <Field label="EMPLOYER ADDRESS" value={f.employer_address} onChange={(v) => set('employer_address', v)} testid="input-employer-address" />
        </div>
        <Field label="MANAGER / SUPERVISOR NAME" value={f.manager_name} onChange={(v) => set('manager_name', v)} testid="input-manager" />
      </div>

      <SectionHeader>JOB SEARCH PREFERENCES</SectionHeader>
      <div className="grid md:grid-cols-2 gap-6">
        <Field label="NOTICE PERIOD" value={f.notice_period} onChange={(v) => set('notice_period', v)} placeholder="e.g. 2 weeks, 1 month" testid="input-notice" />
        <Field label="AVAILABILITY / START DATE" type="date" value={f.availability_date} onChange={(v) => set('availability_date', v)} testid="input-availability" />
        <Field label="EXPECTED SALARY" value={f.expected_salary} onChange={(v) => set('expected_salary', v)} placeholder="e.g. $95,000" testid="input-expected-salary" />
        <Select label="WILLING TO RELOCATE" value={f.willing_to_relocate} onChange={(v) => set('willing_to_relocate', v)} options={['Yes', 'No', 'Maybe']} testid="select-relocate" />
      </div>

      <SectionHeader>SKILLS</SectionHeader>
      <div className="flex gap-2 mb-2">
        <Input
          value={skillInput}
          onChange={(e) => setSkillInput(e.target.value)}
          placeholder="Add a skill"
          className="font-mono"
          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
          data-testid="skill-input"
        />
        <Button type="button" variant="outline" onClick={addSkill} data-testid="add-skill-btn"><Plus className="w-4 h-4" /></Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {f.skills.map((s, i) => (
          <span key={i} className="px-2 py-1 bg-secondary text-sm font-mono flex items-center gap-2">
            {s}
            <button type="button" onClick={() => removeSkill(i)} className="text-muted-foreground hover:text-foreground">×</button>
          </span>
        ))}
      </div>

      <SectionHeader>COVER LETTER (default template)</SectionHeader>
      <Textarea
        value={f.cover_letter}
        onChange={(e) => set('cover_letter', e.target.value)}
        placeholder="Your default cover letter. Extension will paste this into 'Why do you want this job?' style fields."
        className="font-mono min-h-[180px]"
        data-testid="input-cover-letter"
      />

      <Button type="submit" disabled={saving} className="brutalist-shadow-sm uppercase text-xs tracking-wider" data-testid="save-work-btn">
        <Save className="w-4 h-4 mr-2" /> {saving ? 'SAVING...' : 'SAVE WORK'}
      </Button>
    </form>
  );
}

function FinancialsForm({ data, onSaved }) {
  const [f, setF] = useState({
    annual_income: data.annual_income || 0,
    gross_weekly_income: data.gross_weekly_income || 0,
    net_weekly_income: data.net_weekly_income || 0,
    income_source: data.income_source || '',
    bank_name: data.bank_name || '',
    account_bsb: data.account_bsb || '',
    account_number: data.account_number || '',
    savings_balance: data.savings_balance || 0,
    monthly_expenses: data.monthly_expenses || 0,
    other_income: data.other_income || '',
    credit_score_range: data.credit_score_range || '',
    tax_id: data.tax_id || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...f };
      ['annual_income', 'gross_weekly_income', 'net_weekly_income', 'savings_balance', 'monthly_expenses'].forEach((k) => {
        payload[k] = parseFloat(payload[k]) || 0;
      });
      await axios.put(`${API}/profile/financials`, payload, { withCredentials: true });
      toast.success('Financials saved');
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="financials-form">
      <SectionHeader>INCOME</SectionHeader>
      <div className="grid md:grid-cols-2 gap-6">
        <Field label="ANNUAL INCOME (gross)" type="number" value={f.annual_income} onChange={(v) => set('annual_income', v)} testid="input-annual-income" />
        <Field label="INCOME SOURCE" value={f.income_source} onChange={(v) => set('income_source', v)} placeholder="e.g. Salary, Freelance" testid="input-income-source" />
        <Field label="GROSS WEEKLY INCOME" type="number" value={f.gross_weekly_income} onChange={(v) => set('gross_weekly_income', v)} testid="input-weekly-gross" />
        <Field label="NET WEEKLY INCOME (take-home)" type="number" value={f.net_weekly_income} onChange={(v) => set('net_weekly_income', v)} testid="input-weekly-net" />
        <Field label="OTHER INCOME" value={f.other_income} onChange={(v) => set('other_income', v)} placeholder="Rental income, dividends, etc." testid="input-other-income" />
      </div>

      <SectionHeader>BANKING</SectionHeader>
      <div className="grid md:grid-cols-2 gap-6">
        <Field label="BANK NAME" value={f.bank_name} onChange={(v) => set('bank_name', v)} testid="input-bank" />
        <Field label="BSB" value={f.account_bsb} onChange={(v) => set('account_bsb', v)} placeholder="062-000" testid="input-bsb" />
        <Field label="ACCOUNT NUMBER" value={f.account_number} onChange={(v) => set('account_number', v)} testid="input-account-number" />
        <Field label="SAVINGS BALANCE" type="number" value={f.savings_balance} onChange={(v) => set('savings_balance', v)} testid="input-savings" />
        <Field label="MONTHLY EXPENSES" type="number" value={f.monthly_expenses} onChange={(v) => set('monthly_expenses', v)} testid="input-monthly-exp" />
        <Field label="TAX FILE NUMBER (TFN) / TAX ID" value={f.tax_id} onChange={(v) => set('tax_id', v)} testid="input-tfn" />
        <Field label="CREDIT SCORE RANGE" value={f.credit_score_range} onChange={(v) => set('credit_score_range', v)} placeholder="e.g. 700-750" testid="input-credit" />
      </div>

      <Button type="submit" disabled={saving} className="brutalist-shadow-sm uppercase text-xs tracking-wider" data-testid="save-financials-btn">
        <Save className="w-4 h-4 mr-2" /> {saving ? 'SAVING...' : 'SAVE FINANCIALS'}
      </Button>
    </form>
  );
}

function RentalHistoryForm({ data, onSaved }) {
  const [items, setItems] = useState(data && data.length ? data : [{}]);
  const [saving, setSaving] = useState(false);

  const setItem = (i, k, v) => {
    const next = items.slice();
    next[i] = { ...next[i], [k]: v };
    setItems(next);
  };
  const addItem = () => setItems([...items, {}]);
  const removeItem = (i) => setItems(items.filter((_, x) => x !== i));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const clean = items.filter((i) => i.address || i.landlord_name || i.landlord_phone);
      await axios.put(`${API}/profile/rental-history`, { rental_history: clean }, { withCredentials: true });
      toast.success(`Saved ${clean.length} rental${clean.length === 1 ? '' : 's'}`);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="rental-history-form">
      <p className="text-xs text-muted-foreground">{'Add your previous rentals — most recent first. Rental applications will use item #1 as your "previous address".'}</p>

      {items.map((it, i) => (
        <div key={i} className="border border-border p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">RENTAL #{i + 1}</div>
            {items.length > 1 && (
              <button type="button" onClick={() => removeItem(i)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-rental-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Field label="ADDRESS" value={it.address} onChange={(v) => setItem(i, 'address', v)} testid={`rental-${i}-address`} />
            </div>
            <Field label="LANDLORD / AGENT NAME" value={it.landlord_name} onChange={(v) => setItem(i, 'landlord_name', v)} testid={`rental-${i}-landlord`} />
            <Field label="LANDLORD / AGENT PHONE" type="tel" value={it.landlord_phone} onChange={(v) => setItem(i, 'landlord_phone', v)} testid={`rental-${i}-landlord-phone`} />
            <Field label="LANDLORD EMAIL" type="email" value={it.landlord_email} onChange={(v) => setItem(i, 'landlord_email', v)} testid={`rental-${i}-landlord-email`} />
            <Field label="WEEKLY RENT PAID" value={it.weekly_rent} onChange={(v) => setItem(i, 'weekly_rent', v)} placeholder="$650" testid={`rental-${i}-rent`} />
            <Field label="TENANCY START" type="date" value={it.tenancy_start} onChange={(v) => setItem(i, 'tenancy_start', v)} testid={`rental-${i}-start`} />
            <Field label="TENANCY END" type="date" value={it.tenancy_end} onChange={(v) => setItem(i, 'tenancy_end', v)} testid={`rental-${i}-end`} />
            <div className="md:col-span-2">
              <Field label="REASON FOR LEAVING" value={it.reason_for_leaving} onChange={(v) => setItem(i, 'reason_for_leaving', v)} testid={`rental-${i}-reason`} />
            </div>
          </div>
        </div>
      ))}

      <div className="flex gap-3">
        <Button type="button" variant="outline" onClick={addItem} className="uppercase text-xs tracking-wider" data-testid="add-rental-btn">
          <Plus className="w-4 h-4 mr-2" /> ADD RENTAL
        </Button>
        <Button type="submit" disabled={saving} className="brutalist-shadow-sm uppercase text-xs tracking-wider" data-testid="save-rental-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'SAVING...' : 'SAVE RENTAL HISTORY'}
        </Button>
      </div>
    </form>
  );
}

function ReferencesForm({ data, onSaved }) {
  const [items, setItems] = useState(data && data.length ? data : [{}, {}]);
  const [saving, setSaving] = useState(false);
  const setItem = (i, k, v) => { const n = items.slice(); n[i] = { ...n[i], [k]: v }; setItems(n); };
  const addItem = () => setItems([...items, {}]);
  const removeItem = (i) => setItems(items.filter((_, x) => x !== i));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const clean = items.filter((r) => r.name || r.phone || r.email);
      await axios.put(`${API}/profile/references`, { references: clean }, { withCredentials: true });
      toast.success(`Saved ${clean.length} reference${clean.length === 1 ? '' : 's'}`);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="references-form">
      <p className="text-xs text-muted-foreground">Job & rental applications typically ask for 2 references. Add them once — used across every form.</p>
      {items.map((r, i) => (
        <div key={i} className="border border-border p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">REFERENCE #{i + 1}</div>
            {items.length > 1 && (
              <button type="button" onClick={() => removeItem(i)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-ref-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="NAME" value={r.name} onChange={(v) => setItem(i, 'name', v)} testid={`ref-${i}-name`} />
            <Field label="RELATIONSHIP" value={r.relationship} onChange={(v) => setItem(i, 'relationship', v)} placeholder="Former Manager / Landlord / Colleague" testid={`ref-${i}-relationship`} />
            <Field label="COMPANY / ORG" value={r.company} onChange={(v) => setItem(i, 'company', v)} testid={`ref-${i}-company`} />
            <Field label="PHONE" type="tel" value={r.phone} onChange={(v) => setItem(i, 'phone', v)} testid={`ref-${i}-phone`} />
            <div className="md:col-span-2">
              <Field label="EMAIL" type="email" value={r.email} onChange={(v) => setItem(i, 'email', v)} testid={`ref-${i}-email`} />
            </div>
          </div>
        </div>
      ))}
      <div className="flex gap-3">
        <Button type="button" variant="outline" onClick={addItem} className="uppercase text-xs tracking-wider" data-testid="add-ref-btn">
          <Plus className="w-4 h-4 mr-2" /> ADD REFERENCE
        </Button>
        <Button type="submit" disabled={saving} className="brutalist-shadow-sm uppercase text-xs tracking-wider" data-testid="save-ref-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'SAVING...' : 'SAVE REFERENCES'}
        </Button>
      </div>
    </form>
  );
}

function EmergencyContactForm({ data, onSaved }) {
  const [f, setF] = useState({
    name: data.name || '',
    relationship: data.relationship || '',
    phone: data.phone || '',
    email: data.email || '',
    address: data.address || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/profile/emergency-contact`, f, { withCredentials: true });
      toast.success('Emergency contact saved');
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="emergency-form">
      <p className="text-xs text-muted-foreground">Used by gym signups, rental applications, and any onboarding form asking for a next-of-kin.</p>
      <div className="grid md:grid-cols-2 gap-6">
        <Field label="NAME" value={f.name} onChange={(v) => set('name', v)} testid="input-em-name" />
        <Field label="RELATIONSHIP" value={f.relationship} onChange={(v) => set('relationship', v)} placeholder="Parent / Partner / Sibling" testid="input-em-relationship" />
        <Field label="PHONE" type="tel" value={f.phone} onChange={(v) => set('phone', v)} testid="input-em-phone" />
        <Field label="EMAIL" type="email" value={f.email} onChange={(v) => set('email', v)} testid="input-em-email" />
        <div className="md:col-span-2">
          <Field label="ADDRESS (optional)" value={f.address} onChange={(v) => set('address', v)} testid="input-em-address" />
        </div>
      </div>
      <Button type="submit" disabled={saving} className="brutalist-shadow-sm uppercase text-xs tracking-wider" data-testid="save-emergency-btn">
        <Save className="w-4 h-4 mr-2" /> {saving ? 'SAVING...' : 'SAVE CONTACT'}
      </Button>
    </form>
  );
}

/* -------------------------------- exports -------------------------------- */

export default function ProfileTabs({ profile, onSaved }) {
  const [tab, setTab] = useState('identity');
  return (
    <Tabs value={tab} onValueChange={setTab} className="space-y-6">
      <TabsList className="bg-secondary border border-border p-1 flex flex-wrap gap-1 h-auto justify-start">
        <TabsTrigger value="identity" className="uppercase text-xs tracking-wider" data-testid="tab-identity"><User className="w-4 h-4 mr-2" />Identity</TabsTrigger>
        <TabsTrigger value="address" className="uppercase text-xs tracking-wider" data-testid="tab-address"><MapPin className="w-4 h-4 mr-2" />Address</TabsTrigger>
        <TabsTrigger value="work" className="uppercase text-xs tracking-wider" data-testid="tab-work"><Briefcase className="w-4 h-4 mr-2" />Work</TabsTrigger>
        <TabsTrigger value="financials" className="uppercase text-xs tracking-wider" data-testid="tab-financials"><CreditCard className="w-4 h-4 mr-2" />Financials</TabsTrigger>
        <TabsTrigger value="rental" className="uppercase text-xs tracking-wider" data-testid="tab-rental"><HomeIcon className="w-4 h-4 mr-2" />Rental History</TabsTrigger>
        <TabsTrigger value="references" className="uppercase text-xs tracking-wider" data-testid="tab-references"><Users className="w-4 h-4 mr-2" />References</TabsTrigger>
        <TabsTrigger value="emergency" className="uppercase text-xs tracking-wider" data-testid="tab-emergency"><LifeBuoy className="w-4 h-4 mr-2" />Emergency</TabsTrigger>
      </TabsList>

      <TabsContent value="identity" className="border border-border p-6"><IdentityForm data={profile?.identity || {}} onSaved={onSaved} /></TabsContent>
      <TabsContent value="address" className="border border-border p-6"><AddressForm data={profile?.identity || {}} onSaved={onSaved} /></TabsContent>
      <TabsContent value="work" className="border border-border p-6"><WorkForm data={profile?.work || {}} onSaved={onSaved} /></TabsContent>
      <TabsContent value="financials" className="border border-border p-6"><FinancialsForm data={profile?.financials || {}} onSaved={onSaved} /></TabsContent>
      <TabsContent value="rental" className="border border-border p-6"><RentalHistoryForm data={profile?.rental_history || []} onSaved={onSaved} /></TabsContent>
      <TabsContent value="references" className="border border-border p-6"><ReferencesForm data={profile?.references || []} onSaved={onSaved} /></TabsContent>
      <TabsContent value="emergency" className="border border-border p-6"><EmergencyContactForm data={profile?.emergency_contact || {}} onSaved={onSaved} /></TabsContent>
    </Tabs>
  );
}
